import React, { useCallback, useEffect, useState, forwardRef, useImperativeHandle, useRef } from "react";
import useTranslations from "../../hooks/useTranslations";
import { SearchIcon } from "../icons";

type SearchInputProps = {
  onChange?: (value: string) => void;
  onSubmit?: (value: string) => void;
  onFocus?: () => void;
  onBlur?: () => void;
  liveSearch?: boolean;
  label?: string;
  placeholder?: string;
  id?: string;
  value: string;
  className?: string;
};

const SearchInput: React.FC<SearchInputProps> = ({
  onChange,
  onSubmit,
  onFocus,
  onBlur,
  liveSearch = false,
  label,
  id = "search",
  placeholder,
  value = '',
  className = ''
}) => {
  const t = useTranslations();

  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    if (onChange) {
      onChange(e.target.value);
    }
  }, [onChange]);

  /**
   * FIXME: Not sure about the details here, but it seems that, when the SearchInput
   * is placed inside the slideover, the "space" input is captured and not given
   * back to the input (ie: spaces are not registered), stopping bubbling here
   * prevents that.
   */
  const handleStopPropagation = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    e.stopPropagation();
  }, [])

  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && onSubmit) {
      onSubmit(value);
    }
    handleStopPropagation(e);
  }, [handleStopPropagation, onSubmit, value]);

  const handleSubmit = useCallback(() => {
    if (onSubmit) {
      onSubmit(value);
    }
  }, [onSubmit, value]);

  return (
    <div className={className}>
      <label htmlFor="search" className="sr-only">
        {label ? label : t("forms.fields.search")}
      </label>
      <div className="flex rounded-md shadow-sm">
        <div className="relative flex items-stretch flex-grow focus-within:z-10">
          <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
            <SearchIcon className="w-5 h-5 text-gray-400" />
          </div>
          <input
            id={id}
            name="search"
            autoComplete="off"
            value={value}
            onChange={handleChange}
            onFocus={onFocus}
            onBlur={onBlur}
            onKeyDown={liveSearch ? handleStopPropagation : handleKeyDown}
            className={
              `block w-full py-2 pl-10 pr-3 leading-5
                placeholder-gray-500 bg-white border border-gray-300
                focus:outline-none focus:placeholder-gray-400 focus:ring-1
              focus:ring-primary-500 focus:border-primary-500 sm:text-sm
                ${liveSearch ? 'rounded-md' : 'rounded-l-md'}
              `
            }
            placeholder={
              placeholder ? placeholder : (t("forms.fields.search") as string)
            }
            type="search"
          />
        </div>
        {
          !liveSearch && (
            <button
              id="searchButton"
              onClick={handleSubmit}
              className="relative inline-flex items-center px-4 py-2 -ml-px space-x-2 text-sm font-medium border border-gray-300 text-dark-100 rounded-r-md bg-gray-50 hover:bg-gray-100 focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
            >
              <span> {label ? label : t("forms.fields.search")}</span>
            </button>
          )
        }
      </div>
    </div>
  );
}

export default React.memo(SearchInput);
